import React, { useState, useEffect } from 'react';
import StudioBanner from '../../components/studio/StudioBanner';
import { useParams, useNavigate } from 'react-router-dom';
import studioApi from '../../api/studioClient';
const api = studioApi;
import toast from 'react-hot-toast';
import { Plus, Link2, Trash2, Edit3, X, ChevronDown } from 'lucide-react';
import AdminPageHeader from '../../components/studio/AdminPageHeader';
import RelationshipHelp, { HelpButton } from './RelationshipHelp';

const PRESETS = [
  { id: 'peer', icon: '🤝', label: 'Peer / Sibling', desc: 'Records at the same level linked by shared context (co-plaintiffs, co-signers, related cases)',
    defaults: { name: 'Related Case', inverse_name: 'Related Case', sync_model: 'no_sync', anchor_required: false, color: '#6366F1' } },
  { id: 'parent_child', icon: '👆', label: 'Parent → Child', desc: 'One record owns or spawns others (class action → claims, project → tasks)',
    defaults: { name: 'Parent', inverse_name: 'Child', sync_model: 'anchor_controls', anchor_required: true, color: '#0EA5E9' } },
  { id: 'sequential', icon: '➡️', label: 'Sequential', desc: 'One record follows from another (appeal follows case, renewal follows policy)',
    defaults: { name: 'Predecessor', inverse_name: 'Successor', sync_model: 'no_sync', anchor_required: false, color: '#F59E0B' } },
  { id: 'adversarial', icon: '⚔️', label: 'Adversarial / Opposing', desc: 'Records represent opposing sides (plaintiff ↔ defendant, buyer ↔ seller)',
    defaults: { name: 'Opposing Party', inverse_name: 'Opposing Party', sync_model: 'no_sync', anchor_required: false, color: '#EF4444' } },
  { id: 'custom', icon: '✏️', label: 'Custom', desc: 'Start from scratch — define everything yourself',
    defaults: { name: '', inverse_name: '', sync_model: 'no_sync', anchor_required: false, color: '#6B7280' } },
];

const SYNC_MODELS = [
  { value: 'anchor_controls', label: 'Lead record controls', icon: '🔒',
    desc: 'One record is marked as the lead. Shared fields can only be edited on the lead and are read-only on linked records. Best when one case drives the others.' },
  { value: 'free_sync', label: 'Any record can edit', icon: '🔄',
    desc: 'Shared fields can be edited on any linked record. Changes sync to all others automatically. Best when all records are equal partners.' },
  { value: 'no_sync', label: 'View only — no syncing', icon: '👁️',
    desc: 'Records are linked for navigation only. No fields are shared or synced. Best for loose associations.' },
];

export default function RelationshipManager() {
  const { recordTypeId } = useParams();
  const navigate = useNavigate();
  const [relTypes, setRelTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [step, setStep] = useState('preset');
  const [form, setForm] = useState({});
  const [recordTypes, setRecordTypes] = useState([]);
  const [fields, setFields] = useState([]);
  const [recordTypeName, setRecordTypeName] = useState('');
  const [syncPreview, setSyncPreview] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [linkCounts, setLinkCounts] = useState({});

  const fetchData = async () => {
    try {
      const [rtRes, typesRes, fieldsRes] = await Promise.all([
        api.get(`/relationships/types?record_type_id=${recordTypeId}`),
        api.get('/admin/record-types'),
        api.get(`/template-studio/${recordTypeId}/title-config`),
      ]);
      setRelTypes(rtRes.data.relationship_types || []);
      // Fetch link counts (GAP 12)
      try {
        const countsRes = await api.get('/relationships/types-counts');
        setLinkCounts(countsRes.data.counts || {});
      } catch (e) { /* counts are optional */ }
      const allTypes = typesRes.data.record_types || typesRes.data.recordTypes || typesRes.data || [];
      setRecordTypes(Array.isArray(allTypes) ? allTypes : []);
      const rt = (Array.isArray(allTypes) ? allTypes : []).find(t => t.id === recordTypeId);
      setRecordTypeName(rt?.name || '');
      setFields(fieldsRes.data.fields || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [recordTypeId]);

  const openCreate = () => {
    setEditingId(null);
    setStep('preset');
    setForm({ source_record_type_id: recordTypeId, target_record_type_id: recordTypeId, shared_fields: [] });
    setShowModal(true);
    setFieldSearch('');
  };

  const openEdit = (rt) => {
    setEditingId(rt.id);
    setStep('config');
    setForm({
      name: rt.name, inverse_name: rt.inverse_name, pattern: rt.pattern,
      sync_model: rt.sync_model, anchor_required: rt.anchor_required,
      source_record_type_id: rt.source_record_type_id, target_record_type_id: rt.target_record_type_id,
      description: rt.description || '', max_links_per_record: rt.max_links_per_record || '',
      shared_fields: rt.shared_fields || [], color: rt.color || '#6366F1',
    });
    setShowModal(true);
    setFieldSearch('');
  };

  const selectPreset = (preset) => {
    setForm(prev => ({ ...prev, ...preset.defaults, pattern: preset.id }));
    setStep('config');
  };

  const handleSave = async () => {
    if (!form.name || !form.inverse_name) { toast.error('Name and inverse name are required'); return; }
    try {
      if (editingId) {
        await api.put(`/relationships/types/${editingId}`, form);
        toast.success('Relationship type updated');
      } else {
        await api.post('/relationships/types', form);
        toast.success('Relationship type created');
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this relationship type and all its links?')) return;
    try {
      await api.delete(`/relationships/types/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch (err) { toast.error('Delete failed'); }
  };

  const handleSyncPreview = async (rtId) => {
    try {
      const res = await api.post('/relationships/types/' + rtId + '/sync-preview');
      setSyncPreview({ rtId, ...res.data });
    } catch (err) { toast.error('Failed to load preview'); }
  };

  const handleSyncApply = async () => {
    if (!syncPreview) return;
    setSyncing(true);
    try {
      const res = await api.post('/relationships/types/' + syncPreview.rtId + '/sync-apply');
      toast.success(res.data.message || 'Sync complete');
      setSyncPreview(null);
    } catch (err) { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const toggleSharedField = (fieldId) => {
    setForm(prev => {
      const existing = prev.shared_fields || [];
      const has = existing.some(sf => (sf.field_id || sf) === fieldId);
      return {
        ...prev,
        shared_fields: has
          ? existing.filter(sf => (sf.field_id || sf) !== fieldId)
          : [...existing, { field_id: fieldId }],
      };
    });
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <StudioBanner />
      <AdminPageHeader title={<>Record Relationships <HelpButton onClick={() => setShowHelp(true)} /></>} description={`${recordTypeName} — Define how records of this type can link to each other or to other types`} icon={Link2} iconColor="text-gray-500" iconBg="bg-gray-100" backTo="/template-studio">
        <button onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">
          <Plus className="h-4 w-4" /> Add Relationship Type
        </button>
      </AdminPageHeader>

      {/* List */}
      {relTypes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Link2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-700 mb-1">No relationships configured</h3>
          <p className="text-sm text-gray-400 mb-4">Add a relationship type to allow linking records of this type</p>
          <button onClick={openCreate}
            className="px-4 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700">
            Add Relationship Type
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {relTypes.map(rt => {
            const syncInfo = SYNC_MODELS.find(s => s.value === rt.sync_model) || SYNC_MODELS[2];
            return (
              <div key={rt.id} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: rt.color || '#6366F1' }}>
                      <Link2 className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">{rt.name}</h3>
                        <span className="text-gray-300">↔</span>
                        <h3 className="text-sm font-semibold text-gray-900">{rt.inverse_name}</h3>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span className="px-2 py-0.5 bg-gray-100 rounded-full">{rt.pattern || 'custom'}</span>
                        <span>{syncInfo.icon} {syncInfo.label}</span>
                        {rt.anchor_required && <span className="text-amber-600">⚓ Anchor required</span>}
                        {rt.max_links_per_record && <span>Max {rt.max_links_per_record} links</span>}
                        {linkCounts[rt.id] > 0 && <span className="text-brand-600 font-medium">{linkCounts[rt.id]} active link{linkCounts[rt.id] !== 1 ? 's' : ''}</span>}
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                        <span>{rt.source_record_type_name || recordTypeName}</span>
                        <span>→</span>
                        <span>{rt.target_record_type_name || recordTypeName}</span>
                      </div>
                      {rt.description && <p className="text-xs text-gray-400 mt-1">{rt.description}</p>}
                      {rt.shared_fields && rt.shared_fields.length > 0 && (
                        <div className="flex items-center gap-1 mt-2">
                          <span className="text-[10px] text-gray-400 mr-1">Shared:</span>
                          {rt.shared_fields.map((sf, i) => {
                            const fId = sf.field_id || sf;
                            const f = fields.find(fl => fl.id === fId);
                            return <span key={i} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{f?.label || fId}</span>;
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(rt)} className="p-2 text-gray-400 hover:text-brand-600 rounded-lg hover:bg-gray-50">
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(rt.id)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-50">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {rt.sync_model !== 'no_sync' && rt.shared_fields && rt.shared_fields.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">Sync applies to future links only</span>
                    <button onClick={() => handleSyncPreview(rt.id)}
                      className="text-xs font-medium text-brand-600 hover:text-brand-700 flex items-center gap-1">
                      🔄 Sync Now {linkCounts[rt.id] > 0 && <span className="text-gray-400">({linkCounts[rt.id]} link{linkCounts[rt.id] !== 1 ? 's' : ''})</span>}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">
                {editingId ? 'Edit Relationship Type' : step === 'preset' ? 'Choose a Template' : 'Configure Relationship'}
              <HelpButton onClick={() => setShowHelp(true)} />
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            {step === 'preset' && !editingId ? (
              <div className="p-5 space-y-2">
                {PRESETS.map(p => (
                  <button key={p.id} onClick={() => selectPreset(p)}
                    className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-brand-400 hover:bg-brand-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{p.icon}</span>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">{p.label}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{p.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-5 space-y-4">
                {/* Names */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Name (source → target)</label>
                    <input type="text" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="e.g., Co-Plaintiff" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Inverse Name (target → source)</label>
                    <input type="text" value={form.inverse_name || ''} onChange={e => setForm(p => ({ ...p, inverse_name: e.target.value }))}
                      placeholder="e.g., Co-Plaintiff" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                </div>

                {/* Record Types */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Source Record Type {editingId && <span className="text-amber-500 text-[10px]">🔒 locked if links exist</span>}</label>
                    <select value={form.source_record_type_id || ''} onChange={e => setForm(p => ({ ...p, source_record_type_id: e.target.value }))}
                      disabled={!!editingId}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm ${editingId ? 'bg-gray-50 cursor-not-allowed' : ''}`}>
                      {recordTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Target Record Type {editingId && <span className="text-amber-500 text-[10px]">🔒 locked if links exist</span>}</label>
                    <select value={form.target_record_type_id || ''} onChange={e => setForm(p => ({ ...p, target_record_type_id: e.target.value }))}
                      disabled={!!editingId}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm ${editingId ? 'bg-gray-50 cursor-not-allowed' : ''}`}>
                      {recordTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Sync Model */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Data Sync Model</label>
                  <div className="space-y-2">
                    {SYNC_MODELS.map(sm => (
                      <label key={sm.value}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          form.sync_model === sm.value ? 'border-brand-400 bg-brand-50' : 'border-gray-200 hover:border-gray-300'
                        }`}>
                        <input type="radio" name="sync_model" value={sm.value}
                          checked={form.sync_model === sm.value}
                          onChange={() => setForm(p => ({ ...p, sync_model: sm.value, anchor_required: sm.value === 'anchor_controls' ? true : p.anchor_required }))}
                          className="mt-0.5" />
                        <div>
                          <span className="text-sm font-medium text-gray-800">{sm.icon} {sm.label}</span>
                          <p className="text-xs text-gray-500 mt-0.5">{sm.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Anchor */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.anchor_required || false}
                    onChange={e => setForm(p => ({ ...p, anchor_required: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-brand-600" />
                  <span className="text-sm text-gray-700">Require an anchor (lead) record in each group</span>
                </label>

                {/* Max Links */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Max links per record (optional)</label>
                  <input type="number" value={form.max_links_per_record || ''} min="1"
                    onChange={e => setForm(p => ({ ...p, max_links_per_record: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder="Unlimited" className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>

                {/* Color */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
                  <input type="color" value={form.color || '#6366F1'}
                    onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                    className="h-8 w-16 rounded cursor-pointer" />
                </div>

                {/* Shared Fields */}
                {form.sync_model !== 'no_sync' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Shared Fields</label>
                    <p className="text-[10px] text-gray-400 mb-2">
                      {form.sync_model === 'anchor_controls'
                        ? 'Selected fields will be pushed from the lead record to linked records (read-only on linked)'
                        : 'Selected fields will sync bidirectionally between all linked records'}
                    </p>
                    {(form.shared_fields || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {(form.shared_fields || []).map(sf => {
                          const fId = sf.field_id || sf;
                          const f = fields.find(fl => fl.id === fId);
                          return (
                            <span key={fId} className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-50 text-brand-700 text-[10px] rounded-full">
                              {f?.label || fId}
                              <button onClick={() => toggleSharedField(fId)} className="hover:text-red-500 ml-0.5">&times;</button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <input type="text" value={fieldSearch} onChange={e => setFieldSearch(e.target.value)}
                      placeholder="Search fields..." className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs mb-2" />
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                      {fields.filter(f => !fieldSearch || f.label.toLowerCase().includes(fieldSearch.toLowerCase()) || f.field_type.toLowerCase().includes(fieldSearch.toLowerCase())).map(f => {
                        const isShared = (form.shared_fields || []).some(sf => (sf.field_id || sf) === f.id);
                        return (
                          <label key={f.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer">
                            <input type="checkbox" checked={isShared} onChange={() => toggleSharedField(f.id)}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-brand-600" />
                            <span className="text-xs text-gray-700">{f.label}</span>
                            <span className="text-[10px] text-gray-400 ml-auto">{f.field_type}</span>
                          </label>
                        );
                      })}
                      {fields.filter(f => !fieldSearch || f.label.toLowerCase().includes(fieldSearch.toLowerCase()) || f.field_type.toLowerCase().includes(fieldSearch.toLowerCase())).length === 0 && (
                        <p className="text-[10px] text-gray-400 text-center py-2">No fields match "{fieldSearch}"</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description (optional)</label>
                  <textarea value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                    rows={2} placeholder="Explain when to use this relationship type..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                  {!editingId && (
                    <button onClick={() => setStep('preset')} className="text-sm text-gray-500 hover:text-gray-700">
                      ← Back to templates
                    </button>
                  )}
                  <div className="flex items-center gap-2 ml-auto">
                    <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                    <button onClick={handleSave}
                      className="px-5 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">
                      {editingId ? 'Save Changes' : 'Create'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Help Modal */}
      {showHelp && <RelationshipHelp onClose={() => setShowHelp(false)} />}

      {/* Sync Preview Modal */}
      {syncPreview && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[70vh] overflow-y-auto">
            <div className="p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Sync Preview</h2>
              <p className="text-sm text-gray-500 mt-1">{syncPreview.message}</p>
            </div>
            {syncPreview.changes && syncPreview.changes.length > 0 ? (
              <div className="p-5">
                <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                  {syncPreview.changes.map((c, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg text-xs">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-700">{c.field_label}</span>
                        <span className="text-gray-400">on</span>
                        <span className="font-medium text-gray-600">{c.target_record}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-red-500 line-through">{c.current_value}</span>
                        <span className="text-gray-400">→</span>
                        <span className="text-green-600 font-medium">{c.new_value}</span>
                      </div>
                      <div className="text-[10px] text-gray-400 mt-1">From: {c.anchor_record}</div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button onClick={() => setSyncPreview(null)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
                  <button onClick={handleSyncApply} disabled={syncing}
                    className="px-5 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
                    {syncing ? 'Syncing...' : 'Apply ' + syncPreview.total_changes + ' Changes'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-5 text-center">
                <p className="text-sm text-green-600 mb-3">✅ All linked records are already in sync</p>
                <button onClick={() => setSyncPreview(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
